// R45: Gedeelde fetcher + cache voor order-regels (incl. afbeeldingen).
// Doel: vervang de "on-demand bij expand" flow door een prefetch-on-scroll
// (IntersectionObserver in OrdersDecoprofTab). De OrderLinesExpansion-
// component leest uit dezelfde cache → expand voelt instant aan zodra de
// tegel ooit in beeld is geweest.
import { supabase } from "@/integrations/supabase/client";

export type ImageSource =
  | "row"
  | "title-fallback"
  | "sku-fallback"
  | "pid-fallback"
  | "cache"
  | "product-api"
  | "sku-api"
  | "none";

export interface Line {
  id: string;
  productid: number | null;
  title: string;
  variant: string;
  unit: string;
  qty: number;
  image: string | null;
  source: ImageSource;
}

const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
const UNIT_TOKEN_RE =
  /(\d+(?:[.,]\d+)?)\s*(liter|ltr|ml|cl|kg|gram|g|stuks|stuk|st|pak|set|rol|cm|mm|m)\b/i;
function extractUnit(...sources: Array<string | null | undefined>): string {
  for (const src of sources) {
    if (!src) continue;
    const m = src.match(UNIT_TOKEN_RE);
    if (!m) continue;
    let unit = m[2].toLowerCase();
    if (unit === "ltr" || unit === "liter" || unit === "l") unit = "L";
    else if (unit === "gram") unit = "g";
    else if (unit === "st" || unit === "stuk" || unit === "stuks") unit = "stuks";
    return `${m[1]} ${unit}`;
  }
  return "stuks";
}

// R63: cache-key gebumpt — UI leest canonical Lightspeed via edge function met
// 30min server-side TTL (pakbon blijft 5min). In-memory Map persist binnen sessie
// → expand <50ms zodra prefetch-on-viewport (R63 debounced) is afgerond.
const CACHE_KEY = (id: number) => `r63:${id}`;
export const lineCache = new Map<string, Line[]>();
const inflight = new Map<string, Promise<Line[]>>();

export function getCachedLines(orderid: number): Line[] | null {
  return lineCache.get(CACHE_KEY(orderid)) ?? null;
}

export function fetchOrderLines(orderid: number, ordernumber?: string): Promise<Line[]> {
  const k = CACHE_KEY(orderid);
  const cached = lineCache.get(k);
  if (cached) return Promise.resolve(cached);
  const ex = inflight.get(k);
  if (ex) return ex;

  const p = (async (): Promise<Line[]> => {
    const t0 = performance.now();
    // R62: switch UI source from vervuilde orders_live_ls naar canonical Lightspeed
    // via edge function get-canonical-order-products (gedeeld 5min cache met pakbon).
    const { data: edgeData, error } = await supabase.functions.invoke(
      "get-canonical-order-products",
      { body: { orderid, ordernumber: ordernumber ?? String(orderid) } },
    );
    if (error) throw error;
    const raw = (((edgeData as any)?.rows) ?? []) as any[];

    const missingTitles = Array.from(new Set(
      raw.filter((r) => !r.image_url && !r.product_afbeelding_url && r.producttitle)
        .map((r) => (r.producttitle as string).trim()),
    ));
    const missingSkus = Array.from(new Set(
      raw.filter((r) => !r.image_url && !r.product_afbeelding_url && r.sku)
        .map((r) => r.sku as string),
    ));
    const missingProductIds = Array.from(new Set(
      raw.filter((r) => !r.image_url && !r.product_afbeelding_url && r.product_id)
        .map((r) => r.product_id as string),
    ));
    const numericMissingPids = missingProductIds
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);

    const tFb = performance.now();
    const [titleRes, skuRes, pidRes, cachePidRes, cacheSkuRes] = await Promise.all([
      missingTitles.length
        ? supabase.from("orders_live_ls").select("producttitle, image_url")
            .in("producttitle", missingTitles).not("image_url", "is", null).limit(1000)
        : Promise.resolve({ data: [] as any[], error: null } as any),
      missingSkus.length
        ? supabase.from("orders_live_ls").select("sku, image_url")
            .in("sku", missingSkus).not("image_url", "is", null).limit(1000)
        : Promise.resolve({ data: [] as any[], error: null } as any),
      missingProductIds.length
        ? supabase.from("orders_live_ls").select("product_id, image_url")
            .in("product_id", missingProductIds).not("image_url", "is", null).limit(1000)
        : Promise.resolve({ data: [] as any[], error: null } as any),
      numericMissingPids.length
        ? supabase.from("product_image_cache" as any).select("productid, image_url")
            .in("productid", numericMissingPids)
        : Promise.resolve({ data: [] as any[], error: null } as any),
      missingSkus.length
        ? supabase.from("product_image_cache" as any).select("sku, image_url")
            .in("sku", missingSkus)
        : Promise.resolve({ data: [] as any[], error: null } as any),
    ]);
    const fbDur = Math.round(performance.now() - tFb);

    const titleMap = new Map<string, string>();
    const skuMap = new Map<string, string>();
    const pidMap = new Map<string, string>();
    const cachePidMap = new Map<string, string>();
    const cacheSkuMap = new Map<string, string>();
    const apiPidMap = new Map<string, string>();
    const apiSkuMap = new Map<string, string>();

    for (const r of ((titleRes as any).data ?? []) as any[]) {
      const k2 = norm(r.producttitle);
      if (k2 && r.image_url && !titleMap.has(k2)) titleMap.set(k2, r.image_url);
    }
    for (const r of ((skuRes as any).data ?? []) as any[]) {
      if (r.sku && r.image_url && !skuMap.has(r.sku)) skuMap.set(r.sku, r.image_url);
    }
    for (const r of ((pidRes as any).data ?? []) as any[]) {
      if (r.product_id && r.image_url && !pidMap.has(r.product_id)) pidMap.set(r.product_id, r.image_url);
    }
    for (const r of ((cachePidRes as any).data ?? []) as any[]) {
      if (r.image_url) cachePidMap.set(String(r.productid), r.image_url);
    }
    for (const r of ((cacheSkuRes as any).data ?? []) as any[]) {
      if (r.sku && r.image_url) cacheSkuMap.set(r.sku, r.image_url);
    }

    const stillMissingPids = numericMissingPids.filter((pid) => {
      const sPid = String(pid);
      return !pidMap.has(sPid) && !cachePidMap.has(sPid);
    });
    const stillMissingSkus = missingSkus.filter(
      (sku) => !skuMap.has(sku) && !cacheSkuMap.has(sku),
    );

    let coldApiCalls = 0;
    let apiDur = 0;
    if (stillMissingPids.length > 0 || stillMissingSkus.length > 0) {
      coldApiCalls = stillMissingPids.length + stillMissingSkus.length;
      const tApi = performance.now();
      try {
        const { data: apiResp } = await supabase.functions.invoke(
          "get-lightspeed-product-image",
          { body: { productids: stillMissingPids, skus: stillMissingSkus } },
        );
        const results: Record<string, string | null> = (apiResp as any)?.results ?? {};
        const resultsSku: Record<string, string | null> = (apiResp as any)?.results_sku ?? {};
        for (const [pid, url] of Object.entries(results)) if (url) apiPidMap.set(String(pid), url);
        for (const [sku, url] of Object.entries(resultsSku)) if (url) apiSkuMap.set(sku, url);
      } catch (e) {
        console.warn(`[OrderLines ${orderid}] R45 api throw`, e);
      }
      apiDur = Math.round(performance.now() - tApi);
    }

    const mapped: Line[] = raw.map((r) => {
      let image: string | null = null;
      let source: ImageSource = "none";
      const sPid = r.product_id ? String(r.product_id) : null;
      if (r.image_url) { image = r.image_url; source = "row"; }
      else if (r.product_afbeelding_url) { image = r.product_afbeelding_url; source = "row"; }
      else if (r.producttitle && titleMap.has(norm(r.producttitle))) {
        image = titleMap.get(norm(r.producttitle))!; source = "title-fallback";
      } else if (r.sku && skuMap.has(r.sku)) {
        image = skuMap.get(r.sku)!; source = "sku-fallback";
      } else if (sPid && pidMap.has(sPid)) {
        image = pidMap.get(sPid)!; source = "pid-fallback";
      } else if (sPid && cachePidMap.has(sPid)) {
        image = cachePidMap.get(sPid)!; source = "cache";
      } else if (r.sku && cacheSkuMap.has(r.sku)) {
        image = cacheSkuMap.get(r.sku)!; source = "cache";
      } else if (sPid && apiPidMap.has(sPid)) {
        image = apiPidMap.get(sPid)!; source = "product-api";
      } else if (r.sku && apiSkuMap.has(r.sku)) {
        image = apiSkuMap.get(r.sku)!; source = "sku-api";
      }
      return {
        id: r.id,
        productid: r.product_id ? Number(r.product_id) : null,
        title: r.producttitle ?? "Onbekend product",
        variant: r.varianttitle ?? "",
        unit: extractUnit(r.varianttitle, r.producttitle),
        qty: r.quantityordered ?? 1,
        image,
        source,
      };
    });

    const warm = mapped.filter((m) => m.source !== "none" && m.source !== "product-api" && m.source !== "sku-api").length;
    const total = Math.round(performance.now() - t0);
    const avgApiMs = coldApiCalls > 0 ? Math.round(apiDur / Math.max(1, coldApiCalls)) : 0;
    console.log(
      `[OrderLines ${orderid}] R45 cold/warm summary: warmFromCache=${warm} coldApiCalls=${coldApiCalls} avgApiMs=${avgApiMs}ms fallbackParallel=${fbDur}ms total=${total}ms rows=${mapped.length}`,
    );

    lineCache.set(k, mapped);
    return mapped;
  })();

  inflight.set(k, p);
  p.finally(() => inflight.delete(k));
  return p;
}

// Eenvoudige throttle voor prefetch — max N parallel.
let active = 0;
const queue: Array<() => void> = [];
const MAX_CONCURRENT = 5;
function runNext() {
  while (active < MAX_CONCURRENT && queue.length > 0) {
    const job = queue.shift()!;
    active++;
    job();
  }
}

export function prefetchOrderLines(orderid: number, ordernumber?: string): void {
  const k = CACHE_KEY(orderid);
  if (lineCache.has(k) || inflight.has(k)) return;
  queue.push(() => {
    fetchOrderLines(orderid, ordernumber).catch(() => {}).finally(() => {
      active--;
      runNext();
    });
  });
  runNext();
}
